import { useState, useEffect } from "react";
import { DashboardLayout } from "../components/dashboard/dashboard-layout";
import ProtectedRoute from "../components/ProtectedRoute";
import { PageHeader } from "../components/dashboard/page-header";
import profileService, { ProfileData, ProfileUpdateData, PasswordChangeData, SessionData } from "../services/profileService";
import authService from "../services/auth";
import { useAuth } from "../context/AuthContext";
import { ValidatedInput, ValidatedTextarea } from "../components/forms/ValidatedInput";
import { LocationPicker } from "../components/forms/LocationPicker";
import { PhoneInput } from "../components/forms/PhoneInput";
import { validateProfileForm, validateName, validateBirthday, validateBio, validatePassword } from "../utils/validation";
import { getUserInitials, getUserDisplayName } from "../utils/userUtils";
import {
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Plus,
  Download,
  Lock,
  Bell,
  Shield,
  CreditCard,
  Edit,
  Save,
  X,
  Camera,
  LogOut,
  AlertCircle
} from "lucide-react";

function ProfileContent() {
  const { updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState("personal");
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Real user data
  const [userData, setUserData] = useState<ProfileData | null>(null);
  const [formData, setFormData] = useState<ProfileUpdateData>({});
  const [passwordData, setPasswordData] = useState<PasswordChangeData>({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [sessions, setSessions] = useState<SessionData[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string>('US');
  const [validationErrors, setValidationErrors] = useState<any[]>([]);
  const [isFormValid, setIsFormValid] = useState(true);

  // Load profile data on component mount
  useEffect(() => {
    loadProfileData();
  }, []);

  const loadProfileData = async () => {
    try {
      setLoading(true);
      setError(null);
      const profile = await profileService.getProfile();
      setUserData(profile);
      setFormData({
        firstName: profile.firstName,
        lastName: profile.lastName,
        name: profile.name,
        phone: profile.phone,
        location: profile.location,
        birthday: profile.birthday,
        bio: profile.bio
      });
    } catch (err: any) {
      setError(err.message || 'Failed to load profile data');
      console.error('Error loading profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadSessions = async () => {
    try {
      const sessionData = await profileService.getSessions();
      setSessions(sessionData.sessions);
    } catch (err: any) {
      console.error('Error loading sessions:', err);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    const newFormData = {
      ...formData,
      [field]: value
    };
    setFormData(newFormData);

    // Real-time validation
    if (editMode) {
      const validation = validateProfileForm(newFormData, selectedCountry);
      setValidationErrors(validation.errors);
      setIsFormValid(validation.isValid);
    }
  };

  const handlePasswordChange = (field: keyof PasswordChangeData, value: string) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Validate form data
      const validation = validateProfileForm(formData, selectedCountry);
      setValidationErrors(validation.errors);
      setIsFormValid(validation.isValid);

      if (!validation.isValid) {
        setError(`Please fix the following errors: ${validation.errors.map(e => e.message).join(', ')}`);
        setSaving(false);
        return;
      }

      const updatedProfile = await profileService.updateProfile(formData);
      setUserData(updatedProfile);

      // Update the user data in AuthContext so it reflects across the app
      // Only update fields that exist in the User interface
      updateUser({
        name: updatedProfile.name || `${updatedProfile.firstName || ''} ${updatedProfile.lastName || ''}`.trim(),
        profilePic: updatedProfile.profilePic
      });

      setEditMode(false);
      setSuccess('Profile updated successfully!');

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Validate passwords
      const errors: string[] = [];

      if (!passwordData.currentPassword) {
        errors.push('Current password is required');
      }

      const newPasswordError = validatePassword(passwordData.newPassword);
      if (newPasswordError) {
        errors.push(newPasswordError.message);
      }

      if (passwordData.newPassword !== passwordData.confirmPassword) {
        errors.push('New passwords do not match');
      }

      if (passwordData.currentPassword === passwordData.newPassword) {
        errors.push('New password must be different from current password');
      }

      if (errors.length > 0) {
        setError(errors.join('. '));
        setSaving(false);
        return;
      }

      await profileService.changePassword(passwordData);
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
      });
      setSuccess('Password changed successfully!');

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const newAvatarUrl = await profileService.uploadAvatar(file);
      if (userData) {
        const updatedUserData = {
          ...userData,
          profilePic: newAvatarUrl
        };
        setUserData(updatedUserData);

        // Update the user data in AuthContext so it reflects across the app
        updateUser({
          profilePic: newAvatarUrl
        });
      }
      setSuccess('Avatar updated successfully!');

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to upload avatar');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
      window.location.href = '/login';
    } catch (err: any) {
      console.error('Logout error:', err);
      // Force redirect even if logout fails
      window.location.href = '/login';
    }
  };

  const handleRevokeSession = async (sessionId: string) => {
    try {
      await profileService.revokeSession(sessionId);
      setSessions(prev => prev.filter(session => session.id !== sessionId));
      setSuccess('Session revoked successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to revoke session');
    }
  };

  // Load sessions when security tab is active
  useEffect(() => {
    if (activeTab === 'security') {
      loadSessions();
    }
  }, [activeTab]);

  // Validate form when edit mode is enabled
  useEffect(() => {
    if (editMode) {
      const validation = validateProfileForm(formData, selectedCountry);
      setValidationErrors(validation.errors);
      setIsFormValid(validation.isValid);
    } else {
      setValidationErrors([]);
      setIsFormValid(true);
    }
  }, [editMode, selectedCountry]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-gray-100 mx-auto mb-4"></div>
            <p className="text-gray-500 dark:text-gray-400">Loading profile...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!userData) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <p className="text-red-500 mb-4">Failed to load profile data</p>
            <button
              onClick={loadProfileData}
              className="px-4 py-2 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-lg hover:bg-gray-800 dark:hover:bg-white transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <PageHeader
        title="Profile"
        description="Manage your account settings and preferences"
        tabs={[
          { id: "personal", label: "Personal Info" },
          { id: "security", label: "Security" },
          { id: "notifications", label: "Notifications" },
          { id: "billing", label: "Billing" },
        ]}
        defaultTab="personal"
        onTabChange={(tabId) => setActiveTab(tabId)}
      />

      {/* Error/Success Messages */}
      {error && (
        <div className="mb-6 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
          <p className="text-green-600 dark:text-green-400 text-sm">{success}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800 p-5">
            <div className="flex flex-col items-center">
              <div className="relative mb-4">
                {userData.profilePic ? (
                  <img
                    src={userData.profilePic}
                    alt="Profile"
                    className="h-24 w-24 rounded-full object-cover"
                  />
                ) : (
                  <div className="h-24 w-24 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-2xl font-bold">
                    {getUserInitials(userData)}
                  </div>
                )}
                <label className="absolute bottom-0 right-0 h-8 w-8 rounded-full bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 flex items-center justify-center hover:bg-gray-800 dark:hover:bg-white transition-all duration-200 cursor-pointer">
                  <Camera className="h-4 w-4" />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarUpload}
                    className="hidden"
                    disabled={saving}
                  />
                </label>
              </div>

              <h3 className="text-xl font-bold mb-1">{getUserDisplayName(userData)}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 capitalize">{userData.role}</p>

              <div className="w-full border-t border-gray-200 dark:border-gray-800 pt-4 mt-2">
                <div className="flex items-center gap-3 mb-3">
                  <Mail className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                  <span className="text-sm">{userData.email}</span>
                </div>
                {userData.phone && (
                  <div className="flex items-center gap-3 mb-3">
                    <Phone className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                    <span className="text-sm">{userData.phone}</span>
                  </div>
                )}
                {userData.location && (
                  <div className="flex items-center gap-3 mb-3">
                    <MapPin className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                    <span className="text-sm">{userData.location}</span>
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <Calendar className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                  <span className="text-sm">
                    Joined {new Date(userData.joinDate).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                </div>
              </div>

              <button
                onClick={handleLogout}
                disabled={saving}
                className="w-full mt-6 flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <LogOut className="h-4 w-4" />
                <span>{saving ? 'Signing Out...' : 'Sign Out'}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-2">
          {activeTab === "personal" && (
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800">
              <div className="p-5 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
                <h3 className="text-lg font-semibold">Personal Information</h3>
                {!editMode ? (
                  <button
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200"
                    onClick={() => setEditMode(true)}
                  >
                    <Edit className="h-4 w-4" />
                    <span>Edit</span>
                  </button>
                ) : (
                  <div className="flex items-center gap-2">
                    <button
                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200"
                      onClick={() => setEditMode(false)}
                    >
                      <X className="h-4 w-4" />
                      <span>Cancel</span>
                    </button>
                    <div className="relative">
                      <button
                        className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={handleSaveProfile}
                        disabled={saving || !isFormValid}
                        title={!isFormValid ? 'Please fix validation errors before saving' : 'Save your profile changes'}
                      >
                        <Save className="h-4 w-4" />
                        <span>{saving ? 'Saving...' : 'Save Changes'}</span>
                      </button>
                      {!isFormValid && validationErrors.length > 0 && (
                        <div className="absolute -top-2 -right-2">
                          <div className="w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                            <span className="text-white text-xs font-bold">{validationErrors.length}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="p-5">
                {/* Validation Summary */}
                {editMode && validationErrors.length > 0 && (
                  <div className="mb-6 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                      <h4 className="text-sm font-medium text-red-600 dark:text-red-400">
                        Please fix the following errors:
                      </h4>
                    </div>
                    <ul className="text-sm text-red-600 dark:text-red-400 space-y-1">
                      {validationErrors.map((error, index) => (
                        <li key={index} className="flex items-center gap-2">
                          <span className="w-1 h-1 bg-red-400 rounded-full"></span>
                          {error.message}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    {editMode ? (
                      <ValidatedInput
                        label="First Name"
                        value={formData.firstName || ''}
                        onChange={(value) => handleInputChange('firstName', value)}
                        onValidate={(value) => validateName(value, 'First name')}
                        placeholder="Enter your first name"
                        disabled={saving}
                        maxLength={50}
                        helpText="Only letters, spaces, hyphens, and apostrophes allowed"
                      />
                    ) : (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          First Name
                        </label>
                        <p className="text-sm">{userData.firstName || 'Not set'}</p>
                      </div>
                    )}
                  </div>

                  <div>
                    {editMode ? (
                      <ValidatedInput
                        label="Last Name"
                        value={formData.lastName || ''}
                        onChange={(value) => handleInputChange('lastName', value)}
                        onValidate={(value) => validateName(value, 'Last name')}
                        placeholder="Enter your last name"
                        disabled={saving}
                        maxLength={50}
                        helpText="Only letters, spaces, hyphens, and apostrophes allowed"
                      />
                    ) : (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Last Name
                        </label>
                        <p className="text-sm">{userData.lastName || 'Not set'}</p>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Email Address
                    </label>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{userData.email}</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Email cannot be changed</p>
                  </div>

                  <div>
                    {editMode ? (
                      <PhoneInput
                        label="Phone Number"
                        value={formData.phone || ''}
                        onChange={(value) => handleInputChange('phone', value)}
                        onCountryChange={(countryCode) => setSelectedCountry(countryCode)}
                        defaultCountry={selectedCountry}
                        disabled={saving}
                        helpText="Select your country from the dropdown, then enter your local phone number (country code is added automatically)"
                      />
                    ) : (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Phone Number
                        </label>
                        <p className="text-sm">{userData.phone || 'Not set'}</p>
                      </div>
                    )}
                  </div>

                  <div>
                    {editMode ? (
                      <LocationPicker
                        label="Location"
                        value={formData.location || ''}
                        onChange={(value) => handleInputChange('location', value)}
                        onCountryChange={(countryCode) => setSelectedCountry(countryCode)}
                        disabled={saving}
                        helpText="Select your country and city, or enter a custom location"
                      />
                    ) : (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Location
                        </label>
                        <p className="text-sm">{userData.location || 'Not set'}</p>
                      </div>
                    )}
                  </div>

                  <div>
                    {editMode ? (
                      <ValidatedInput
                        label="Birthday"
                        type="date"
                        value={formData.birthday || ''}
                        onChange={(value) => handleInputChange('birthday', value)}
                        onValidate={validateBirthday}
                        disabled={saving}
                        helpText="Enter your birth date (MM/DD/YYYY or YYYY-MM-DD)"
                      />
                    ) : (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Birthday
                        </label>
                        <p className="text-sm">{userData.birthday || 'Not set'}</p>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Role
                    </label>
                    <p className="text-sm capitalize">{userData.role}</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Role cannot be changed</p>
                  </div>
                </div>

                <div>
                  {editMode ? (
                    <ValidatedTextarea
                      label="Bio"
                      value={formData.bio || ''}
                      onChange={(value) => handleInputChange('bio', value)}
                      onValidate={validateBio}
                      placeholder="Tell us about yourself..."
                      disabled={saving}
                      maxLength={500}
                      rows={4}
                      helpText="Share a brief description about yourself (optional)"
                    />
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Bio
                      </label>
                      <p className="text-sm">{userData.bio || 'No bio added yet.'}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === "security" && (
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800">
              <div className="p-5 border-b border-gray-200 dark:border-gray-800">
                <h3 className="text-lg font-semibold">Security Settings</h3>
              </div>

              <div className="p-5">
                <div className="mb-6">
                  <h4 className="text-md font-medium mb-4">Change Password</h4>
                  <div className="space-y-4">
                    <ValidatedInput
                      label="Current Password"
                      type="password"
                      value={passwordData.currentPassword}
                      onChange={(value) => handlePasswordChange('currentPassword', value)}
                      placeholder="Enter current password"
                      disabled={saving}
                      required={true}
                      helpText="Enter your current password to verify your identity"
                    />

                    <ValidatedInput
                      label="New Password"
                      type="password"
                      value={passwordData.newPassword}
                      onChange={(value) => handlePasswordChange('newPassword', value)}
                      onValidate={validatePassword}
                      placeholder="Enter new password"
                      disabled={saving}
                      required={true}
                      helpText="Must contain at least 8 characters with uppercase, lowercase, number and special character"
                    />

                    <ValidatedInput
                      label="Confirm New Password"
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(value) => handlePasswordChange('confirmPassword', value)}
                      onValidate={(value) => {
                        if (!value) return { field: 'confirmPassword', message: 'Please confirm your new password' };
                        if (value !== passwordData.newPassword) return { field: 'confirmPassword', message: 'Passwords do not match' };
                        return null;
                      }}
                      placeholder="Confirm new password"
                      disabled={saving}
                      required={true}
                      helpText="Re-enter your new password to confirm"
                    />
                    <button
                      onClick={handleChangePassword}
                      disabled={saving || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                      className="px-4 py-2 rounded-lg bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {saving ? 'Updating...' : 'Update Password'}
                    </button>
                  </div>
                </div>

                <div className="pt-6 border-t border-gray-200 dark:border-gray-800">
                  <h4 className="text-md font-medium mb-4">Two-Factor Authentication</h4>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="font-medium">Two-factor authentication is disabled</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Add an extra layer of security to your account</p>
                    </div>
                    <button className="px-4 py-2 rounded-lg bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-white transition-all duration-200">
                      Enable
                    </button>
                  </div>
                </div>

                <div className="pt-6 border-t border-gray-200 dark:border-gray-800">
                  <h4 className="text-md font-medium mb-4">Sessions</h4>
                  <div className="space-y-4">
                    {sessions.length > 0 ? (
                      sessions.map((session) => (
                        <div key={session.id} className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-800">
                          <div>
                            <p className="font-medium">
                              {session.device} {session.isCurrent && '(Current)'}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {session.location} • {new Date(session.timestamp).toLocaleDateString()}
                            </p>
                            <p className="text-xs text-gray-400 dark:text-gray-500">
                              Login method: {session.loginMethod} • IP: {session.ipAddress}
                            </p>
                          </div>
                          {session.isCurrent ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                              Active
                            </span>
                          ) : (
                            <button
                              onClick={() => handleRevokeSession(session.id)}
                              disabled={saving}
                              className="text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Revoke
                            </button>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8">
                        <p className="text-gray-500 dark:text-gray-400">No session data available</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "notifications" && (
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800">
              <div className="p-5 border-b border-gray-200 dark:border-gray-800">
                <h3 className="text-lg font-semibold">Notification Preferences</h3>
              </div>

              <div className="p-5">
                <div className="space-y-6">
                  <div>
                    <h4 className="text-md font-medium mb-4">Email Notifications</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">Account activity</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">Get notified about important account activities</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input type="checkbox" className="sr-only peer" defaultChecked />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-gray-900 dark:peer-checked:bg-gray-100"></div>
                        </label>
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">New features</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">Get notified about new features and updates</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input type="checkbox" className="sr-only peer" defaultChecked />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-gray-900 dark:peer-checked:bg-gray-100"></div>
                        </label>
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">Marketing emails</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">Receive promotional offers and newsletters</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input type="checkbox" className="sr-only peer" />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-gray-900 dark:peer-checked:bg-gray-100"></div>
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="pt-6 border-t border-gray-200 dark:border-gray-800">
                    <h4 className="text-md font-medium mb-4">Push Notifications</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">New messages</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">Get notified when you receive new messages</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input type="checkbox" className="sr-only peer" defaultChecked />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-gray-900 dark:peer-checked:bg-gray-100"></div>
                        </label>
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">Task updates</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">Get notified about task assignments and updates</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input type="checkbox" className="sr-only peer" defaultChecked />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-gray-900 dark:peer-checked:bg-gray-100"></div>
                        </label>
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">Reminders</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">Get notified about upcoming deadlines and events</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input type="checkbox" className="sr-only peer" />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-gray-900 dark:peer-checked:bg-gray-100"></div>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "billing" && (
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800">
              <div className="p-5 border-b border-gray-200 dark:border-gray-800">
                <h3 className="text-lg font-semibold">Billing Information</h3>
              </div>

              <div className="p-5">
                <div className="mb-6">
                  <h4 className="text-md font-medium mb-4">Current Plan</h4>
                  <div className="flex items-center justify-between p-4 rounded-lg border border-gray-200 dark:border-gray-800">
                    <div>
                      <p className="font-medium">Professional Plan</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">$29/month • Renews on June 15, 2025</p>
                    </div>
                    <button className="px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200">
                      Change Plan
                    </button>
                  </div>
                </div>

                <div className="pt-6 border-t border-gray-200 dark:border-gray-800 mb-6">
                  <h4 className="text-md font-medium mb-4">Payment Methods</h4>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 rounded-lg border border-gray-200 dark:border-gray-800">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800">
                          <CreditCard className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                        </div>
                        <div>
                          <p className="font-medium">Visa ending in 4242</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">Expires 12/2025</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                          Default
                        </span>
                        <button className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
                          Edit
                        </button>
                      </div>
                    </div>
                    <button className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200">
                      <Plus className="h-4 w-4" />
                      <span>Add Payment Method</span>
                    </button>
                  </div>
                </div>

                <div className="pt-6 border-t border-gray-200 dark:border-gray-800">
                  <h4 className="text-md font-medium mb-4">Billing History</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200 dark:border-gray-800">
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Invoice</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Date</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Amount</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Status</th>
                          <th className="text-right py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Download</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-gray-200 dark:border-gray-800">
                          <td className="py-3 px-4 text-sm">#INV-001</td>
                          <td className="py-3 px-4 text-sm">May 15, 2025</td>
                          <td className="py-3 px-4 text-sm">$29.00</td>
                          <td className="py-3 px-4 text-sm">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                              Paid
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-right">
                            <button className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
                              <Download className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                        <tr className="border-b border-gray-200 dark:border-gray-800">
                          <td className="py-3 px-4 text-sm">#INV-002</td>
                          <td className="py-3 px-4 text-sm">Apr 15, 2025</td>
                          <td className="py-3 px-4 text-sm">$29.00</td>
                          <td className="py-3 px-4 text-sm">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                              Paid
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-right">
                            <button className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
                              <Download className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                        <tr>
                          <td className="py-3 px-4 text-sm">#INV-003</td>
                          <td className="py-3 px-4 text-sm">Mar 15, 2025</td>
                          <td className="py-3 px-4 text-sm">$29.00</td>
                          <td className="py-3 px-4 text-sm">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                              Paid
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-right">
                            <button className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
                              <Download className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

export default function ProfilePage() {
  return (
    <ProtectedRoute requireOnboarding={true}>
      <ProfileContent />
    </ProtectedRoute>
  );
}